import React, { useState } from 'react';
import Button from './Button';
import ConfirmDialog from './ConfirmDialog';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

export type AdminDeleteEntity = 'event' | 'club' | 'shop' | 'vehicle' | 'user';

export interface AdminDeleteButtonProps {
  entity: AdminDeleteEntity;
  id: number;
  /** Value the admin must type (slug, name, username, or vehicle slug/id) */
  confirmToken: string;
  label?: string;
  description?: React.ReactNode;
  buttonLabel?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  onDeleted?: (summary: Record<string, unknown>) => void;
}

function summarizeCounts(summary: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(summary)) {
    if (typeof value === 'number' && value > 0 && key !== 'vehicle_details') {
      parts.push(`${key.replace(/_/g, ' ')}: ${value}`);
    }
  }
  return parts.slice(0, 6).join(' · ') || 'related rows cleaned up';
}

/**
 * Danger delete control with typed confirmation for super-admin hard deletes.
 */
export const AdminDeleteButton: React.FC<AdminDeleteButtonProps> = ({
  entity,
  id,
  confirmToken,
  label,
  description,
  buttonLabel = 'Delete',
  size = 'sm',
  className,
  disabled,
  onDeleted,
}) => {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [loading, setLoading] = useState(false);

  const token = confirmToken.trim();
  const matches = typed.trim().localeCompare(token, undefined, { sensitivity: 'accent' }) === 0;

  const reset = () => {
    setOpen(false);
    setTyped('');
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!matches || loading) return;
    setLoading(true);
    try {
      const result = await apiService.adminDeleteEntity(entity, id, typed.trim());
      showToast(`${label || entity} deleted. ${summarizeCounts(result.summary)}`, 'success');
      onDeleted?.(result.summary);
      reset();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : `Failed to delete ${entity}`, 'error');
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="danger"
        size={size}
        className={className}
        disabled={disabled || !token}
        onClick={() => {
          setTyped('');
          setOpen(true);
        }}
      >
        {buttonLabel}
      </Button>
      <ConfirmDialog
        isOpen={open}
        title={`Permanently delete this ${entity}?`}
        description={
          description || (
            <>
              This hard-deletes the {entity}
              {label ? (
                <>
                  {' '}
                  <strong className="text-white">{label}</strong>
                </>
              ) : null}
              . Related child rows are removed or detached; surviving clubs, events, and accounts are
              preserved where applicable. This cannot be undone.
            </>
          )
        }
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        destructive
        confirmDisabled={!matches}
        loading={loading}
        loadingLabel="Deleting…"
        closeOnOverlayClick={!loading}
        onCancel={() => {
          if (!loading) reset();
        }}
        onConfirm={() => void handleConfirm()}
      >
        <label htmlFor={`admin-delete-confirm-${entity}-${id}`} className="input-label mt-3">
          Type <span className="font-mono text-orange-300">{token}</span> to confirm
        </label>
        <input
          id={`admin-delete-confirm-${entity}-${id}`}
          type="text"
          className="input w-full"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={loading}
          aria-required
        />
        {!matches && typed.trim() !== '' ? (
          <p className="text-xs text-amber-400/90 mt-2" role="status">
            Confirmation text does not match yet.
          </p>
        ) : null}
      </ConfirmDialog>
    </>
  );
};

export default AdminDeleteButton;
